import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { mediaSrc, avatarSrc, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import NewChatModal from "../components/NewChatModal";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { Plus, Send, ImagePlus, Loader2, ArrowLeft, Users, MessageCircle, Search } from "lucide-react";
import { toast } from "sonner";

function shortTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 86400 && d.getDate() === new Date().getDate()) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (s < 604800) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
}

export default function Chat() {
  const { id } = useParams();
  const [convos, setConvos] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState("");

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConvos(data);
    } catch { /* keep last list */ } finally { setLoadingList(false); }
  }, []);

  useEffect(() => {
    loadList();
    const t = setInterval(loadList, 10000);
    return () => clearInterval(t);
  }, [loadList]);

  const shown = convos.filter((c) => !filter || (c.title || "").toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="max-w-[1200px] mx-auto px-0 sm:px-6 lg:px-8 sm:pt-4 sm:pb-6" data-testid="chat-page">
      <div className="flex h-[calc(100dvh-8.5rem)] md:h-[calc(100dvh-6.5rem)] sm:border sm:border-white/10 sm:rounded-2xl bg-[#111827] overflow-hidden">
        <aside className={`w-full sm:w-[320px] shrink-0 border-r border-white/10 flex flex-col bg-[#182132] ${id ? "hidden sm:flex" : "flex"}`}>
          <div className="flex items-center justify-between px-4 h-14 border-b border-white/10 shrink-0">
            <h1 className="font-heading text-lg text-white">Messages</h1>
            <button data-testid="new-chat-btn" onClick={() => setShowNew(true)} className="w-9 h-9 rounded-full bg-[#0066FF] hover:bg-[#0052CC] text-white flex items-center justify-center transition-colors" aria-label="New chat"><Plus size={18} /></button>
          </div>
          <div className="px-3 py-2.5 border-b border-white/10">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <input data-testid="chat-search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search chats" className="w-full bg-[#0B101D] border border-white/10 rounded-full pl-8 pr-3 py-2 text-sm text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#0066FF]" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar" data-testid="conversation-list">
            {loadingList ? (
              <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#0066FF]" size={24} /></div>
            ) : shown.length === 0 ? (
              <div className="text-center py-16 px-4" data-testid="chat-empty">
                <MessageCircle size={32} className="mx-auto text-[#334155] mb-3" />
                <p className="text-white font-semibold mb-4">{filter ? "No matches" : "No chats yet"}</p>
                {!filter && <button onClick={() => setShowNew(true)} className="bg-[#0066FF] hover:bg-[#0052CC] text-white font-semibold text-sm px-5 py-2 rounded-full transition-colors">Start a chat</button>}
              </div>
            ) : shown.map((c) => (
              <Link key={c.id} to={`/chat/${c.id}`} data-testid={`conversation-item-${c.id}`}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${id === c.id ? "bg-[#0066FF]/15 border-l-2 border-[#0066FF]" : "hover:bg-white/[0.04] border-l-2 border-transparent"}`}>
                <ConvAvatar c={c} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm truncate flex items-center gap-1 ${c.unread > 0 ? "text-white font-bold" : "text-[#E2E8F0] font-semibold"}`}>
                      <span className="truncate">{c.title}</span>{c.type === "group" && <Users size={12} className="text-[#64748B] shrink-0" />}
                    </p>
                    <span className="text-[11px] text-[#64748B] shrink-0">{shortTime(c.last_message_at)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-xs truncate ${c.unread > 0 ? "text-[#CBD5E1]" : "text-[#64748B]"}`}>{c.last_message_preview || "No messages yet"}</p>
                    {c.unread > 0 && <span className="min-w-[20px] h-5 px-1.5 bg-[#0066FF] text-white text-[10px] font-bold flex items-center justify-center rounded-full" data-testid={`conversation-unread-${c.id}`}>{c.unread}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </aside>

        <section className={`flex-1 min-w-0 ${id ? "flex" : "hidden sm:flex"} flex-col`}>
          {id ? <Thread key={id} convId={id} onActivity={loadList} /> : (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
              <MessageCircle size={44} className="text-[#334155] mb-4" />
              <p className="text-white font-semibold text-lg">Your messages</p>
              <button onClick={() => setShowNew(true)} className="mt-4 bg-[#0066FF] hover:bg-[#0052CC] text-white font-semibold text-sm px-5 py-2 rounded-full transition-colors">New message</button>
            </div>
          )}
        </section>
      </div>

      {showNew && <NewChatModal onClose={() => setShowNew(false)} onCreated={loadList} />}
    </div>
  );
}

function ConvAvatar({ c, size = "w-11 h-11" }) {
  if (c.avatar_url) return <img src={avatarSrc(c.avatar_url)} alt={c.title} className={`${size} rounded-full object-cover bg-[#0B101D] shrink-0`} />;
  if (c.type === "group") return <div className={`${size} rounded-full bg-[#0B101D] border border-white/10 flex items-center justify-center text-[#0066FF] shrink-0`}><Users size={18} /></div>;
  return <div className={`${size} rounded-full bg-[#0066FF] flex items-center justify-center font-semibold text-white shrink-0`}>{c.title?.[0]?.toUpperCase()}</div>;
}

function Thread({ convId, onActivity }) {
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const fileRef = useRef();
  const scrollRef = useRef();
  const lastTs = useRef(null);
  const activity = useRef(onActivity);
  activity.current = onActivity;

  const scrollToBottom = () => requestAnimationFrame(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  });
  const markRead = () => api.post(`/conversations/${convId}/read`).then(() => activity.current?.()).catch(() => {});

  useEffect(() => {
    let active = true;
    setConv(null); setMessages([]); lastTs.current = null;
    api.get(`/conversations/${convId}`).then(({ data }) => { if (active) setConv(data); }).catch(() => nav("/chat"));
    api.get(`/conversations/${convId}/messages`).then(({ data }) => {
      if (!active) return;
      setMessages(data);
      lastTs.current = data.length ? data[data.length - 1].created_at : new Date(0).toISOString();
      scrollToBottom();
      markRead();
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId, nav]);

  useEffect(() => {
    const poll = async () => {
      if (!lastTs.current || document.hidden) return;
      try {
        const { data } = await api.get(`/conversations/${convId}/messages`, { params: { after: lastTs.current } });
        if (data.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            return [...prev, ...data.filter((d) => !seen.has(d.id))];
          });
          lastTs.current = data[data.length - 1].created_at;
          scrollToBottom();
          markRead();
        }
      } catch { /* retry next tick */ }
    };
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  const appendOwn = (msg) => {
    setMessages((prev) => (prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]));
    lastTs.current = msg.created_at;
    scrollToBottom();
    activity.current?.();
  };

  const sendText = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${convId}/messages`, { text: body });
      setText("");
      appendOwn(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const sendImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image")) return toast.error("Only images can be sent");
    if (file.size > 500 * 1024 * 1024) return toast.error("Image exceeds the 500 MB limit");
    setUploadPct(0);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/conversations/${convId}/attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
        onUploadProgress: (evt) => { if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100)); },
      });
      appendOwn(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploadPct(null); }
  };

  if (!conv) return <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-[#0066FF]" size={28} /></div>;
  const other = conv.type === "dm" ? conv.members.find((m) => m.id !== me.id) : null;

  return (
    <>
      <header className="flex items-center gap-3 px-3 sm:px-4 h-14 border-b border-white/10 shrink-0 bg-[#182132]">
        <button onClick={() => nav("/chat")} className="sm:hidden w-9 h-9 rounded-full flex items-center justify-center text-[#94A3B8] hover:text-white hover:bg-white/10" data-testid="thread-back-btn" aria-label="Back"><ArrowLeft size={18} /></button>
        <ConvAvatar c={conv} size="w-9 h-9" />
        <div className="min-w-0">
          {other ? (
            <Link to={`/u/${other.username}`} data-testid="thread-title" className="text-white text-sm font-semibold truncate flex items-center gap-1 hover:underline">
              <span className="truncate">{conv.title}</span><VerifiedBadge show={other.verified} size={13} />
            </Link>
          ) : <p data-testid="thread-title" className="text-white text-sm font-semibold truncate">{conv.title}</p>}
          <p className="text-[11px] text-[#64748B] truncate">
            {other ? `@${other.username}` : `${conv.member_count} members · ${conv.members.map((m) => "@" + m.username).slice(0, 4).join(", ")}`}
          </p>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto custom-scrollbar px-3 sm:px-5 py-4 bg-[#0B101D]" data-testid="messages-container">
        {messages.length === 0 && <p className="text-center text-[#64748B] text-sm py-10" data-testid="thread-empty">No messages yet</p>}
        {messages.map((m, i) => {
          const own = m.sender_id === me.id;
          const prev = messages[i - 1];
          const newDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
          const grouped = prev && !newDay && prev.sender_id === m.sender_id;
          return (
            <div key={m.id}>
              {newDay && <div className="flex justify-center my-4"><span className="text-[11px] text-[#64748B] bg-[#182132] px-3 py-1 rounded-full">{dayLabel(m.created_at)}</span></div>}
              <div data-testid={`message-${m.id}`} className={`flex gap-2 ${own ? "flex-row-reverse" : ""} ${grouped ? "mt-0.5" : "mt-3"}`}>
                {!own && (
                  <div className="w-7 shrink-0 self-end">
                    {!grouped && (m.sender_avatar
                      ? <img src={avatarSrc(m.sender_avatar)} alt="" className="w-7 h-7 rounded-full object-cover" />
                      : <div className="w-7 h-7 rounded-full bg-[#1E293B] flex items-center justify-center text-[#94A3B8] text-[10px] font-semibold">{m.sender_username?.[0]?.toUpperCase()}</div>)}
                  </div>
                )}
                <div className={`max-w-[78%] flex flex-col ${own ? "items-end" : "items-start"}`}>
                  {!own && conv.type === "group" && !grouped && <span className="text-[11px] text-[#64748B] mb-0.5 px-1">@{m.sender_username}</span>}
                  <div title={new Date(m.created_at).toLocaleString()} className={`px-3.5 py-2 text-sm rounded-2xl ${own ? "bg-[#0066FF] text-white rounded-br-md" : "bg-[#182132] text-[#F1F5F9] border border-white/10 rounded-bl-md"}`}>
                    {m.attachment_url && (
                      <a href={mediaSrc(m.attachment_url)} target="_blank" rel="noreferrer" className="block mb-1 -mx-1.5 -mt-0.5">
                        <img src={mediaSrc(m.attachment_url)} alt={m.attachment_name} className="max-w-full max-h-72 object-contain rounded-xl bg-black" />
                      </a>
                    )}
                    {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                  </div>
                  {(!messages[i + 1] || messages[i + 1].sender_id !== m.sender_id) && (
                    <span className="text-[10px] text-[#64748B] mt-1 px-1">{new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {uploadPct !== null && (
        <div className="px-4 py-2 border-t border-white/10 bg-[#182132]" data-testid="chat-upload-progress">
          <div className="flex items-center justify-between mb-1 text-xs"><span className="text-[#94A3B8]">Sending photo…</span><span className="text-[#00E5FF] font-semibold">{uploadPct}%</span></div>
          <div className="h-1 rounded-full bg-[#0B101D]"><div className="h-full rounded-full bg-[#0066FF] transition-all duration-200" style={{ width: `${uploadPct}%` }} /></div>
        </div>
      )}

      <form onSubmit={sendText} className="flex items-end gap-2 p-3 border-t border-white/10 shrink-0 bg-[#182132]">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="chat-file-input" onChange={(e) => { sendImage(e.target.files[0]); e.target.value = ""; }} />
        <button type="button" data-testid="chat-image-btn" onClick={() => fileRef.current?.click()} className="w-10 h-10 rounded-full flex items-center justify-center text-[#94A3B8] hover:text-[#00E5FF] hover:bg-white/10 transition-colors shrink-0" aria-label="Send photo"><ImagePlus size={20} /></button>
        <textarea
          data-testid="chat-message-input"
          value={text}
          rows={1}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
          placeholder="Message…"
          className="flex-1 resize-none max-h-32 bg-[#0B101D] border border-white/10 text-white text-sm px-4 py-2.5 rounded-2xl focus:outline-none focus:border-[#0066FF] placeholder:text-[#64748B]"
        />
        <button type="submit" data-testid="chat-send-btn" disabled={sending || !text.trim()} className="w-10 h-10 rounded-full bg-[#0066FF] hover:bg-[#0052CC] text-white flex items-center justify-center transition-colors disabled:opacity-40 shrink-0" aria-label="Send">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </>
  );
}
